import {
  Alert,
  ButtonGroup,
  ButtonProps,
  closeCurrentDialog,
  DataStatus,
  showNotification,
} from 'platform/components';
import {Grid, GridItem} from 'platform/foundation';

import {includes} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  PatchVehicleAdvertisingSettingsRequestBody,
  useEditGeneralSettingsMutation,
  useGetGeneralSettingsQuery,
  VehicleAdvertisingSettingsResponseBody,
  VehicleGeneralAdvertisingAttributeRequestBody,
  VehicleGeneralAdvertisingSettingsRequestBody,
  VehicleWindshieldAdvertisingSettingsResponseBody,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError, useAdPlatformsVehicleUpdateDialog} from '@dms/shared';
import {$AdvertisingSettingsRequestBody, Form} from '@dms/teas';

import {appendSuffix, buildArray, suffixTestId, TestIdProps} from 'shared/web';

import {AdvertisingAttributeField} from './AdvertisingAttributeField';

export interface AdSettingsEditProps extends TestIdProps {
  vehicleId: string;
  isDisabled: boolean;
}

const SKIPPED_FIELDS = [
  'title_extension',
  'subtitle',
  'description',
  'condition',
  'version',
  'generation',
  'digital_certificate',
  'digital_certificate_theme',
  'video_url',
  'highlights',
  'model_range',
  'trim_line',
];

export function AdSettingsEdit(props: AdSettingsEditProps) {
  const [updateVehicleAds] = useAdPlatformsVehicleUpdateDialog();

  const {
    data: adSettings,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetGeneralSettingsQuery(
    {
      vehicleId: props.vehicleId,
    },
    {
      skip: isNilOrEmpty(props.vehicleId),
      selectFromResult: ({data, ...res}) => ({
        ...res,
        data: {
          ...data,
          general: {
            attributes: data?.general.attributes.filter(
              (attribute) => !includes(attribute.code, SKIPPED_FIELDS)
            ),
          },
        } as VehicleAdvertisingSettingsResponseBody,
      }),
    }
  );

  const [editAdSettings, {isLoading: isEditLoading}] = useEditGeneralSettingsMutation();

  const handleSubmit = async (values: PatchVehicleAdvertisingSettingsRequestBody) => {
    await editAdSettings({
      vehicleId: props.vehicleId,
      patchVehicleAdvertisingSettingsRequestBody: values,
    })
      .unwrap()
      // Keep the dialog open until the tag-invalidation refetch of general
      // settings lands, so the cache holds the freshly-saved value before a
      // quick reopen can read the stale cached value (T20-129071). The refetch
      // is cache-warming only — swallow its failure so a transient refetch
      // error can't mask an already-successful save.
      .then(() =>
        refetch()
          .unwrap()
          .catch(() => {
            // refetch failure is non-fatal; the invalidated tag still triggers
            // a fresh fetch when the dialog next mounts.
          })
      )
      .then(closeCurrentDialog)
      .then(() => {
        showNotification.success(
          i18n.t('general.notifications.advertisement.windshieldDataEditSubtitle')
        );
        updateVehicleAds(props.vehicleId);
      })
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError} isEmpty={isNilOrEmpty(adSettings)}>
      <Form<
        VehicleGeneralAdvertisingSettingsRequestBody,
        VehicleAdvertisingSettingsResponseBody,
        PatchVehicleAdvertisingSettingsRequestBody
      >
        onSubmit={handleSubmit}
        formId={appendSuffix('adSettingsForm', props['data-testid'])}
        schema={$AdvertisingSettingsRequestBody}
        getFormValues={(settings) => ({attributes: settings.general.attributes})}
        getRequestBody={(values) => ({
          general: {
            attributes: values.attributes.map((attribute) => ({
              ...attribute,
              value: attribute.override ? attribute.value : null,
            })) as VehicleGeneralAdvertisingAttributeRequestBody[],
          },
        })}
        initialValues={adSettings}
        render={(formRendererProps) => {
          const {handleSubmit, FieldArray} = formRendererProps;

          return (
            <form onSubmit={handleSubmit}>
              <Grid columns={12}>
                <GridItem span={12}>
                  <Alert
                    data-testid={suffixTestId('adSettings-alert', props)}
                    type="inline"
                    variant="info"
                    title={i18n.t('general.notifications.advertisement.generalDataEdit')}
                  />
                </GridItem>
                <FieldArray<VehicleWindshieldAdvertisingSettingsResponseBody>
                  subscription={{}}
                  name="attributes"
                >
                  {({fields}) =>
                    fields.map((name) => (
                      <AdvertisingAttributeField
                        key={name}
                        name={name}
                        isDisabled={props.isDisabled}
                      />
                    ))
                  }
                </FieldArray>
                <GridItem span={12}>
                  <ButtonGroup
                    align="right"
                    buttons={buildArray<ButtonProps>()
                      .add({
                        title: i18n.t('general.actions.discard'),
                        variant: 'secondary',
                        onClick: closeCurrentDialog,
                      })
                      .whenNot(props.isDisabled, {
                        title: i18n.t('general.actions.confirm'),
                        onClick: handleSubmit,
                        isLoading: isEditLoading || isFetching,
                      })}
                  />
                </GridItem>
              </Grid>
            </form>
          );
        }}
      />
    </DataStatus>
  );
}
